/**
 * Página de Equipamentos - ConfereAI
 * Reestruturada para usar componentes padronizados
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import { ApiResponse } from '../../types/api';
import {
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  DataTable,
  Column,
  StatusBadge,
  ActionButtons,
  StatCard,
  EmptyState,
  FilterPanel,
} from '../../components/Common';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { usePermissions } from '../../hooks/usePermissions';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { SystemModule } from '../../types/permissions';
import ImportModal from '../../components/Common/ImportModal';
import { equipamentosImportConfig } from '../../config/importConfigs';

// Interface alinhada com a estrutura real do banco de dados PostgreSQL
interface EquipmentItem {
  id: number;
  empresa_id: number;
  nome: string;
  tag: string;
  tipo: string;
  localizacao: string | null;
  status: string;
  fabricante: string | null;
  numero_serie: string | null;
  capacidade: string | null;
  criado_em: string;
  atualizado_em: string;
}

const Equipment: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [showImportModal, setShowImportModal] = useState(false);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Hook de infinite scroll
  const {
    items: equipment,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<EquipmentItem>({
    fetchFunction: async (page, limit, filters) => {
      try {
        const response = await apiService.get<ApiResponse<any>>('EQUIPMENTS', {
          page,
          limit,
          status: filters?.status,
          tipo: filters?.tipo
        });

        if (response.success) {
          if (!response.data) {
            return { success: true, data: { items: [], pagination: { total: 0 } } };
          }

          let equipamentosArray: any[];

          if (Array.isArray(response.data)) {
            equipamentosArray = response.data;
          } else if (response.data.equipamentos && Array.isArray(response.data.equipamentos)) {
            equipamentosArray = response.data.equipamentos;
          } else if (response.data.items && Array.isArray(response.data.items)) {
            equipamentosArray = response.data.items;
          } else {
            throw new Error('Estrutura de resposta da API inválida');
          }

          const apiData = equipamentosArray.map((item: any) => ({
            id: item.id,
            empresa_id: item.empresa_id,
            nome: item.nome,
            tag: item.tag,
            tipo: item.tipo,
            localizacao: item.localizacao,
            status: item.status,
            fabricante: item.fabricante,
            numero_serie: item.numero_serie,
            capacidade: item.capacidade,
            criado_em: item.criado_em,
            atualizado_em: item.atualizado_em
          }));

          return {
            success: true,
            data: {
              items: apiData,
              pagination: {
                total: response.data.pagination?.total || apiData.length
              }
            }
          };
        }

        throw new Error(response.message || 'Erro ao carregar equipamentos');
      } catch (error) {
        console.error('Erro ao carregar equipamentos:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar equipamentos'
        };
      }
    },
    itemsPerPage: 10,
    filters: { status: statusFilter, tipo: typeFilter },
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  const filteredEquipment = equipment.filter(item => {
    const matchesSearch = item.nome.toLowerCase().includes(filter.toLowerCase()) ||
                         item.tag.toLowerCase().includes(filter.toLowerCase()) ||
                         (item.localizacao && item.localizacao.toLowerCase().includes(filter.toLowerCase()));
    const matchesStatus = !statusFilter || item.status === statusFilter;
    const matchesType = !typeFilter || item.tipo === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const deleteEquipment = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Tem certeza que deseja excluir este equipamento?')) {
      try {
        const response = await apiService.delete(`EQUIPMENT_BY_ID`, { id }) as ApiResponse<{ message: string }>;
        if (response.success) {
          utils.showNotification('Equipamento excluído com sucesso!', { type: 'success' });
          // Recarrega a lista após deletar
          window.location.reload();
        } else {
          throw new Error(response.message || 'Erro ao excluir equipamento');
        }
      } catch (error) {
        console.error('Erro ao excluir equipamento:', error);
        utils.showNotification('Erro ao excluir equipamento', { type: 'error' });
      }
    }
  };

  const getEquipmentTypes = () => {
    const types = Array.from(new Set(equipment.map(e => e.tipo)));
    return types;
  };

  // Definição das colunas da tabela
  const columns: Column<EquipmentItem>[] = [
    {
      header: 'Código',
      accessor: 'tag',
      render: (value: string) => <strong>{value}</strong>,
    },
    {
      header: 'Nome',
      accessor: 'nome',
      render: (value: string) => <strong>{value}</strong>,
    },
    {
      header: 'Tipo',
      accessor: 'tipo',
      render: (value: string) => (
        <span className="status-badge status-info">
          {value}
        </span>
      ),
    },
    {
      header: 'Localização',
      accessor: 'localizacao',
      render: (value: string | null) => value || '-',
    },
    {
      header: 'Fabricante',
      accessor: 'fabricante',
      render: (value: string | null) => value || '-',
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (value: string) => {
        // Capitaliza primeira letra para exibição
        const statusDisplay = value.charAt(0).toUpperCase() + value.slice(1);
        return <StatusBadge status={statusDisplay} />;
      },
    },
    {
      header: 'Ações',
      accessor: (row: EquipmentItem) => (
        <ActionButtons
          onView={() => navigate(`/equipamentos/visualizar/${row.id}`)}
          onEdit={() => navigate(`/equipamentos/editar/${row.id}`)}
          onDelete={() => deleteEquipment(row.id)}
          showEdit={canUpdate(SystemModule.EQUIPAMENTOS)}
          showDelete={canDelete(SystemModule.EQUIPAMENTOS)}
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="page-container">
        <PageHeader title="Equipamentos" buttonText="Novo Equipamento" />
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando equipamentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <PageHeader title="Equipamentos">
        <div style={{ display: 'flex', gap: '10px' }}>
          {canCreate(SystemModule.EQUIPAMENTOS) && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-excel"></i>
              Importar Planilha
            </button>
          )}
          {canCreate(SystemModule.EQUIPAMENTOS) && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/equipamentos/novo')}
            >
              <i className="fas fa-plus"></i>
              Novo Equipamento
            </button>
          )}
        </div>
      </PageHeader>

      <div className="page-content">
        <Card>
          <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Cadastro de Equipamentos</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Nome, TAG ou localização...'
                },
                {
                  type: 'select',
                  label: 'Tipo',
                  value: typeFilter,
                  onChange: setTypeFilter,
                  options: [{ value: '', label: 'Todos' }, ...getEquipmentTypes().map(type => ({ value: type, label: type }))]
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    { value: 'ativo', label: 'Ativo' },
                    { value: 'inativo', label: 'Inativo' },
                    { value: 'manutencao', label: 'Manutenção' }
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setStatusFilter('');
                setTypeFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Equipamentos"
            />
          </CardHeader>

          <CardBody>
            {/* Contador de resultados */}
            {filteredEquipment.length > 0 && (
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="equipamentos"
              />
            )}

            {filteredEquipment.length === 0 ? (
              <EmptyState
                icon="fas fa-tools"
                title="Nenhum equipamento encontrado"
                description={filter || statusFilter || typeFilter ? 'Tente ajustar os filtros de busca.' : 'Cadastre os primeiros equipamentos do sistema.'}
                actionText="Cadastrar Primeiro Equipamento"
                onAction={() => navigate('/equipamentos/novo')}
              />
            ) : (
              <>
                <DataTable<EquipmentItem>
                  columns={columns}
                  data={filteredEquipment}
                  emptyMessage="Nenhum equipamento encontrado"
                  isLoadingMore={isLoading}
                />
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </>
            )}
          </CardBody>
        </Card>

        {/* Estatísticas dos equipamentos */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <StatCard
            icon="fas fa-tools"
            value={equipment.filter(e => e.status === 'ativo').length}
            label="Equipamentos Ativos"
            color="primary"
          />
          <StatCard
            icon="fas fa-wrench"
            value={equipment.filter(e => e.status === 'manutencao').length}
            label="Em Manutenção"
            color="info"
          />
          <StatCard
            icon="fas fa-ban"
            value={equipment.filter(e => e.status === 'inativo').length}
            label="Inativos"
            color="warning"
          />
        </div>
      </div>

      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => window.location.reload()}
        config={equipamentosImportConfig}
      />
    </div>
  );
};

export default Equipment;
